/**
 * Infinite Scroll for Collection Pages
 * Automatically loads more products as user scrolls down
 */

class InfiniteScroll {
  constructor() {
    this.trigger = document.getElementById('infinite-scroll-trigger');
    this.productGrid = document.getElementById('product-grid');
    this.isLoading = false;
    this.hasMorePages = false;
    this.nextPageUrl = null;
    this.currentPage = 1;
    this.totalPages = 1;

    if (!this.trigger || !this.productGrid) {
      return; // No pagination, exit
    }

    this.nextPageUrl = this.trigger.dataset.nextPage;
    this.currentPage = parseInt(this.trigger.dataset.currentPage) || 1;
    this.totalPages = parseInt(this.trigger.dataset.totalPages) || 1;
    this.hasMorePages = this.currentPage < this.totalPages && this.nextPageUrl;

    if (!this.hasMorePages) {
      return; // No more pages to load
    }

    this.init();
  }

  init() {
    // Create intersection observer for scroll detection
    const options = {
      root: null,
      rootMargin: '200px', // Start loading 200px before reaching the trigger
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
          this.loadNextPage();
        }
      });
    }, options);

    this.observer.observe(this.trigger);
  }

  async loadNextPage() {
    if (this.isLoading || !this.hasMorePages) {
      return;
    }

    this.isLoading = true;
    this.showLoadingIndicator();

    try {
      const response = await fetch(this.nextPageUrl);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the product grid in the new page
      const newProductGrid = doc.getElementById('product-grid');
      if (!newProductGrid) {
        this.hasMorePages = false;
        this.hideLoadingIndicator();
        return;
      }

      // Extract product items
      const newProducts = newProductGrid.querySelectorAll('li.grid__item');
      if (newProducts.length === 0) {
        this.hasMorePages = false;
        this.hideLoadingIndicator();
        return;
      }

      // Get the skip_styles flag from the first product to maintain consistency
      const firstProduct = newProducts[0];
      const skipStyles = firstProduct.querySelector('[data-skip-styles]')?.dataset.skipStyles === 'true';

      // Append new products to the grid
      newProducts.forEach((product, index) => {
        // Update lazy loading for new products
        const images = product.querySelectorAll('img[loading="lazy"]');
        images.forEach(img => {
          img.loading = 'lazy';
        });

        // Update animation order if needed
        if (product.hasAttribute('style') && product.style.getPropertyValue('--animation-order')) {
          const currentIndex = this.productGrid.children.length + index + 1;
          product.style.setProperty('--animation-order', currentIndex);
        }

        this.productGrid.appendChild(product);
      });

      // Find the new pagination trigger in the loaded page
      const newTrigger = doc.getElementById('infinite-scroll-trigger');
      if (newTrigger) {
        this.nextPageUrl = newTrigger.dataset.nextPage;
        this.currentPage = parseInt(newTrigger.dataset.currentPage) || this.currentPage + 1;
        this.totalPages = parseInt(newTrigger.dataset.totalPages) || this.totalPages;
        this.hasMorePages = this.currentPage < this.totalPages && this.nextPageUrl;
      } else {
        this.hasMorePages = false;
      }

      // Reinitialize scroll animations if they exist
      if (window.Shopify && window.Shopify.designMode) {
        // In theme editor, refresh animations
        this.refreshAnimations();
      }

    } catch (error) {
      console.error('Error loading next page:', error);
      this.hasMorePages = false;
    } finally {
      this.isLoading = false;
      this.hideLoadingIndicator();
    }
  }

  showLoadingIndicator() {
    let loader = document.getElementById('infinite-scroll-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.id = 'infinite-scroll-loader';
      loader.className = 'infinite-scroll-loader';
      loader.innerHTML = `
        <div class="loading-spinner">
          <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 20 20">
            <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-dasharray="31.416" stroke-dashoffset="31.416">
              <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416;0 31.416" repeatCount="indefinite"/>
              <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416;-31.416" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>
        <p class="infinite-scroll-loading-text">Loading more products...</p>
      `;
      this.trigger.parentNode.insertBefore(loader, this.trigger);
    }
    loader.style.display = 'block';
  }

  hideLoadingIndicator() {
    const loader = document.getElementById('infinite-scroll-loader');
    if (loader) {
      loader.style.display = 'none';
    }
  }

  refreshAnimations() {
    // Trigger any scroll animations if the theme uses them
    if (window.scrollTrigger && typeof window.scrollTrigger === 'function') {
      window.scrollTrigger();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new InfiniteScroll();
  });
} else {
  new InfiniteScroll();
}
